import React, { useEffect, useRef, useState } from 'react'
import * as cloud from './cloud'
import { displayName } from './auth'
import { checkQuota, recordUsage, quotaMessage } from './quota'
import { PageHeader } from '../PageHeader'
import { ThemeContextValue, tbClass, uiPrimitives, useThemedStyles } from '../theme'
import { makeDefaultScore } from '../defaultScore'
import { UNTITLED_SCORE_TITLE, withDocTitle } from '../docTitle'
import { SiteFooter } from '../SiteFooter'
import { recognizeScoreImage, extractTitleFromNmn } from '../vlmRecognize'
import { musicXmlToNmn } from '../musicxml'
import { setScorePublic } from './plaza'
import { appAlert, appConfirm, appPrompt, AppDialogHost } from '../AppDialog'
import { isPlusUser, planLabel, scoreLimitOf } from './plan'
import { UpgradeDialog } from './UpgradeDialog'
import { fmtExpiry } from './pay'
import { savePitchCalibration } from './profile'
import { CalibratePanel } from '@sparks-notation/react-editor/inspector/practice/CalibratePanel'
import { MicTestDialog } from './MicTestDialog'

/**
 * 编曲练习库 —— 云端曲谱的卡片墙。
 *
 * 版面照 `_v2-designs/jiangnan-qinan.html` 的「编曲练习库」样张：
 * 卡片动作**默认收起、悬停才浮现**（`opacity .55 → 1`），让标题与曲目信息是主角；
 * 窄屏没有悬停这回事，所以断点以下直接把动作用常态 + 加大触控面积。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	// 顶栏按钮 / 导入按钮 / 排序下拉都走全站那一套，别再各写一份
	...uiPrimitives(t),

	lib: {
		padding: '24px 22px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '16px 12px',
		},
	},
	bar: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		marginBottom: 18,
	},
	/** 第一行：标题 + 额度（左），搜索 + 排序（右）。分类页签另起一行（catTabs）。 */
	barTop: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		flexWrap: 'wrap',
	},
	barTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 600,
		whiteSpace: 'nowrap' as const,
	},
	/** 标题与「Plus 会员 56/200 首」之间的竖分隔 */
	barDiv: {
		width: 1,
		height: 14,
		background: t.palette.line2,
		flex: 'none',
	},
	/** 分类页签（全部 / 我的曲谱 / 跟我练100）。独立一行，复用首页来源切换的视觉语言。 */
	catTabs: {
		display: 'inline-flex',
		alignSelf: 'flex-start',
		gap: 4,
		background: t.palette.paper3,
		borderRadius: t.radius.md,
		padding: 3,
	},
	catTab: {
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink2,
		border: 0,
		background: 'none',
		padding: '5px 12px',
		borderRadius: t.radius.sm,
		cursor: 'pointer',
		'&:hover': {
			color: t.palette.ink,
		},
	},
	catTabOn: {
		background: t.palette.paper,
		color: t.palette.ink,
		fontWeight: 600,
		boxShadow: t.shadows.lift,
	},
	/**
	 * 额度 + 会员标识：「免费会员 3/20 首」「Plus 会员 5/200 首」。
	 *
	 * 免费用户可点 → 弹升级对比（这是库里唯一一个"常驻但不打扰"的升级入口，
	 * 比在顶栏再塞一个「升级」按钮克制）。Plus 用户也显示，但不可点 ——
	 * 会员身份本来就该看得见，不是只在升级时才提。
	 */
	quota: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 7,
		// 第一行：标题 + 额度靠左，额度吸走空白把搜索/排序推到最右（分类页签已
		// 挪到第二行，不再参与第一行的空白分配，这里放心吸）。
		marginRight: 'auto',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
		lineHeight: 1,
	},
	quotaClickable: {
		cursor: 'pointer',
	},
	/** 会员名做成小胶囊：免费=素色描边，Plus=朱砂实心 */
	planTag: {
		padding: '3px 7px',
		borderRadius: 999,
		border: `1px solid ${t.palette.line2}`,
		color: t.palette.ink3,
		fontSize: 11,
		whiteSpace: 'nowrap' as const,
	},
	planTagPlus: {
		border: `1px solid ${t.palette.cinnabar}`,
		background: t.palette.cinnabar,
		color: '#fff',
	},
	/** 「3/20」：数字比「已用」更该被读到 */
	quotaNum: {
		fontVariantNumeric: 'tabular-nums',
		color: t.palette.ink,
	},
	/** 已满额：数字转朱砂，点「新建曲谱」会被拦，先给个视觉预警 */
	quotaNumFull: {
		color: t.palette.cinnabar,
		fontWeight: 600,
	},
	/** 搜索框：凹槽底色 + 墨线，视觉上比按钮低一档，不会抢「新建曲谱」 */
	search: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		background: t.palette.paper3,
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		padding: '6px 10px',
		minWidth: 186,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			// 窄屏挤成一行会溢出，让搜索独占一行
			minWidth: 0,
			flex: '1 1 100%',
			order: 10,
		},
	},
	searchIcon: {
		flex: 'none',
		color: t.palette.ink3,
	},
	searchInput: {
		border: 0,
		background: 'none',
		font: 'inherit',
		fontFamily: t.fonts.sans,
		fontSize: 13,
		color: t.palette.ink,
		width: '100%',
		outline: 'none',
		'&::placeholder': {
			color: t.palette.ink3,
		},
	},
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
		gap: 14,
		[`@media (max-width: ${t.breakpoint}px)`]: {
			gridTemplateColumns: '1fr',
		},
	},
	card: {
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		background: t.palette.paper2,
		padding: '15px 15px 12px',
		display: 'flex',
		flexDirection: 'column',
		gap: 9,
		cursor: 'pointer',
		transition: 'border-color .14s, box-shadow .14s',
		'&:hover': {
			borderColor: t.palette.celadon,
			boxShadow: t.shadows.lift,
		},
		// 悬停才让动作浮现（`$cardActs` 是 JSS 的同表引用，不靠 DOM 层级选择器）
		'&:hover $cardActs': {
			opacity: 1,
		},
	},
	cardTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 16,
		fontWeight: 600,
		letterSpacing: '.02em',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	cardMeta: {
		fontFamily: t.fonts.serif,
		fontSize: 11.5,
		color: t.palette.ink3,
		fontVariantNumeric: 'tabular-nums',
		display: 'flex',
		gap: 10,
		flexWrap: 'wrap',
	},
	cardActs: {
		display: 'flex',
		gap: 2,
		marginTop: 2,
		paddingTop: 9,
		borderTop: `1px solid ${t.palette.line}`,
		opacity: 0.55,
		transition: 'opacity .14s',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			// 触屏没有 hover，动作必须常态可见；顺带把点击面积做够
			opacity: 1,
			gap: 0,
		},
	},
	act: {
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink2,
		border: 0,
		background: 'none',
		padding: '3px 7px',
		borderRadius: 2,
		cursor: 'pointer',
		'&:hover': {
			background: t.palette.celadon2,
			color: t.palette.celadonInk,
		},
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '9px 11px',
			fontSize: 13,
		},
	},
	actDanger: {
		'&:hover': {
			background: t.palette.cinnabar + '1a',
			color: t.palette.cinnabar,
		},
	},
	/** 公开态操作用青瓷强调，一眼区分于「删除」等常规动作 */
	actPublic: {
		color: t.palette.celadonInk,
		'&:hover': {
			background: t.palette.celadon2,
			color: t.palette.celadonInk,
		},
	},
	/** 卡片 meta 里的「已公开」徽标 */
	pubBadge: {
		color: t.palette.celadonInk,
		background: t.palette.celadon2,
		borderRadius: 2,
		padding: '1px 6px',
	},
	/** 卡片 meta 里的「跟我练」来源徽标，区分「跟我练100」保存进来的课程 */
	courseBadge: {
		color: t.palette.cinnabar,
		background: t.palette.cinnabar + '1a',
		borderRadius: 2,
		padding: '1px 6px',
	},
	empty: {
		textAlign: 'center',
		padding: '60px 0',
		color: t.palette.ink3,
		fontSize: 13.5,
		lineHeight: 1.8,
	},
	/** 游客草稿迁移提示：青瓷高亮，是"捡回东西"而不是"出错了" */
	migrate: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		flexWrap: 'wrap',
		background: t.palette.celadon2,
		border: `1px solid ${t.palette.celadon}`,
		borderRadius: t.radius.sm,
		padding: '12px 16px',
		marginBottom: 16,
		fontSize: 13.5,
		color: t.palette.ink,
	},
	userBadge: {
		fontSize: 12,
		color: t.palette.ink3,
	},
	/**
	 * 排序下拉。必须 `appearance: none` —— iOS / 微信浏览器会忽略原生 select 的
	 * 背景与边框，直接画成系统控件，那一排就只剩它不是这套配色了。
	 * 关掉原生外观后箭头得自己补一个（.sortArrow），不能往 option 文案里塞。
	 */
	sortWrap: {
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
	},
	sort: {
		appearance: 'none',
		WebkitAppearance: 'none',
		paddingRight: 24,
	},
	sortArrow: {
		position: 'absolute',
		right: 9,
		fontSize: 9,
		color: t.palette.ink3,
		pointerEvents: 'none',
		lineHeight: 1,
	},
})

const useStyles = () => useThemedStyles(stylesFactory)

type ScoreRow = {
	id: string
	title: string
	updated_at: string
	created_at: string | null
	/** 是否公开到广场（仅所有者可改；字段见 supabase/plaza.sql） */
	is_public: boolean
	/** 来源分类："mine" = 我的曲谱，"course" = 跟我练100 保存进来的课程 */
	category: string
}

/**
 * 从「跟我练」课曲谱标题「第 N 课 · 标题」里解析课次号 N。
 * 解析失败返回一个很大的数，让识别不了的排在后面，别抢正序位置。
 */
function courseLessonNo(title: string): number {
	const m = title.match(/^第\s*(\d+)\s*课/)
	return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER
}

export function LibraryPage(props: {
	user: any
	onOpenScore: (id: string) => void
	onLogout: () => void
	/** 点左上角品牌块回首页 */
	onHome?: () => void
	/** 当前用户是管理员时，顶栏多一个「管理」入口（见 admin.ts 里的约定） */
	isAdmin?: boolean
	onAdmin?: () => void
	/** 进「个人设置」页（改昵称 / 头像） */
	onProfile?: () => void
}) {
	const classes = useStyles()
	const [scores, setScores] = useState<ScoreRow[] | null>(null)
	const [search, setSearch] = useState('')
	const [sortBy, setSortBy] = useState<'updated_at' | 'created_at' | 'title'>('updated_at')
	/**
	 * 分类页签：'all' = 全部，'mine' = 我的曲谱，'course' = 跟我练100。
	 * 「跟我练100」保存进来的课程单独一类，不与用户自建/导入的曲谱混在一起。
	 */
	const [catTab, setCatTab] = useState<'all' | 'mine' | 'course'>('all')
	const [localDraft, setLocalDraft] = useState<string | null>(null)
	const [migrating, setMigrating] = useState(false)
	/** 升级对比弹窗（点额度徽章打开；只有免费用户能打开） */
	const [upgradeOpen, setUpgradeOpen] = useState(false)
	/** 「设置」菜单里的两个弹窗：麦克风测试 / 音色校准 */
	const [micTestOpen, setMicTestOpen] = useState(false)
	const [calibrating, setCalibrating] = useState(false)

	async function load() {
		// 排序在库里做：列表可能上百条，前端排会让首屏变慢。
		// 后端固定按 updated_at 倒序返回（RLS 只允许读自己的行，所以不需要
		// 按 user_id 过滤），这里按用户选的档位重排。
		const rows = await cloud.listScores()
		const sorted = sortBy === 'title'
			? [...rows].sort((a, b) => a.title.localeCompare(b.title, 'zh-Hans-CN'))
			: sortBy === 'created_at'
				? [...rows].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
				: rows
		setScores(sorted as ScoreRow[])
	}

	useEffect(() => {
		load()
	}, [sortBy])

	// 检测本地暂存曲谱（登录前试用留下的），提示迁移
	useEffect(() => {
		const draft = localStorage.getItem('sparks-nmn-demo-src')
		if (draft && draft.trim()) setLocalDraft(draft)
	}, [])

	async function migrateLocalDraft() {
		if (!localDraft) return
		setMigrating(true)
		const m = localDraft.match(/^Dt:\s*(.+)$/m)
		const title = (m ? m[1].trim() : '') || '本地曲谱'
		let newId: string
		try {
			// creator 不再由前端传 —— 后端从 Profile.nickname 自动填。
			newId = await cloud.saveNewScore(title, localDraft)
		} catch (err: any) {
			setMigrating(false)
			void appAlert({title: '导入失败', message: err.message, confirmText: '知道了'})
			return
		}
		setMigrating(false)
		localStorage.removeItem('sparks-nmn-demo-src')
		setLocalDraft(null)
		props.onOpenScore(newId)
	}

	function fmtTime(iso: string) {
		const d = new Date(iso)
		const pad = (n: number) => String(n).padStart(2, '0')
		return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
	}

	/**
	 * 新建曲谱：**不问名字，直接进编辑器**。
	 *
	 * 原来这里用原生 `prompt` 问名字，用户还没写一个字就得先回答"这谱子叫什么"，
	 * 多数人这时并不知道，只能把默认值「未命名曲谱」原样确认 —— 库里于是攒一堆
	 * 同名条目；而且原生 `prompt` 在微信/内嵌浏览器里被直接禁用（返回 null 且
	 * 毫无提示），表现成「点了新建没反应」（同一坑见 `AppDialog.appPrompt` 的注释）。
	 *
	 * 现在先建一条**占位曲谱**（库里有名字才能建行），内容是不带 `Dt:` 的起手样张，
	 * 建完直接跳进编辑器。名字留到第一次保存时再问：`TestApp.saveToCloud` 见谱面
	 * 没有曲名就弹取名框，把名字写回 `Dt:` 并更新这条记录 —— 库名与谱面名仍然是
	 * 同一个字符串。
	 *
	 * 为什么不在这一步省掉建库、等保存时再建：那样新谱在刷新/关页前**只活在内存里**
	 *（`#edit` 无 id 走的是本地模式，只有未登录保存才落草稿），关掉标签页就全丢。
	 * 先建行能换来"刷新即恢复"，代价是用户不保存就离开会留一条占位曲谱 ——
	 * 与改动前一致，不是新增的负担。
	 */
	async function createScore() {
		// 配额：先查后做，做成才记（见 saas/quota.ts 与 supabase/quota.sql）
		const quota = await checkQuota('create_score')
		if (!quota.allowed) {
			void appAlert({title: '配额不足', message: quotaMessage('create_score', quota), confirmText: '知道了'})
			return
		}
		try {
			// 内容不带 `Dt:` —— 名字还没定，别在谱面上印一个用户没确认过的名字
			//（原来塞的是「小星星」，于是新建的谱一保存就真叫《小星星》，还因为
			// "已经有名字了"连取名框都不弹）
			const newId = await cloud.saveNewScore(UNTITLED_SCORE_TITLE, makeDefaultScore())
			props.onOpenScore(newId)
		} catch (err: any) {
			void appAlert({title: '创建失败', message: err.message, confirmText: '知道了'})
		}
	}

	async function renameScore(id: string, current: string) {
		// 与新建同一个理由：原生 prompt 在微信/内嵌浏览器里被禁用，
		// 改名失败会静默返回，用户以为「点了改名没反应」。
		const title = await appPrompt({
			title: '修改名称',
			defaultValue: current,
			confirmText: '保存',
			validate: (v: string) => v.trim() ? (v.trim().length > 60 ? '名字太长了（最多 60 字）' : null) : '名字不能为空',
		})
		if (title === null || title.trim() === current) return
		const clean = title.trim()
		try {
			// **连谱面的 `Dt:` 一起改**。只改库名会立刻造出「列表里叫《我的歌》、
			// 打开后谱面还印《小星星》」的错位 —— 那正是用户上一轮提的问题。
			// 改名不产生历史版本（仍是一次普通 update），但内容必须跟着变。
			const detail = await cloud.loadScore(id)
			await cloud.updateScore(id, clean, withDocTitle(detail.content, clean))
			load()
		} catch (err: any) {
			// 读旧内容失败就别硬写，否则可能用空内容覆盖掉用户的作品
			void appAlert({title: '改名失败', message: err?.message ?? '请稍后重试', confirmText: '知道了'})
		}
	}

	async function deleteScore(id: string, title: string) {
		if (!await appConfirm({title: '删除曲谱', message: `确定删除「${title}」？此操作不可恢复。`, confirmText: '删除', cancelText: '取消', danger: true})) return
		await cloud.deleteScore(id)
		load()
	}

	async function copyScore(id: string, title: string) {
		try {
			const original = await cloud.loadScore(id)
			// 副本继承原创建者署名（出处）。后端在 creator 为空时用当前用户昵称兜底，
			// 所以这里只管传内容 —— 前端再拼一份署名只会在用户改过昵称后产生错位。
			await cloud.saveNewScore(title + '（副本）', original.content)
		} catch (err: any) {
			void appAlert({title: '创建副本失败', message: err.message, confirmText: '知道了'})
			return
		}
		load()
	}

	// ---------- 公开 / 私密（广场） ----------
	// 《曲谱公开分享免责声明》在**注册时**就与《用户服务协议》一并勾选过了
	// （见 `AuthPage` 的协议行），所以这里点「公开到广场」直接生效，不再弹
	// 任何确认框 —— 每次公开都甩一份声明让人重读，实际效果只能是闭眼勾。
	// 「取消公开」（转私密）同样直接生效：收窄权限不需要用户额外承担什么。
	function requestPublic(score: ScoreRow) {
		void applyPublic(score.id, !score.is_public)
	}

	async function applyPublic(id: string, isPublic: boolean) {
		try {
			await setScorePublic(id, isPublic)
			load()
		} catch (err: any) {
			void appAlert({title: '操作失败', message: String(err?.message ?? err), confirmText: '知道了'})
		}
	}

	// ---------- 导入本地 .spnmn ----------
	const spnmnInputRef = useRef<HTMLInputElement>(null)
	const [importing, setImporting] = useState(false)

	async function handleSpnmnImport(evt: React.ChangeEvent<HTMLInputElement>) {
		const file = evt.target.files?.[0]
		if (!file) return
		setImporting(true)
		try {
			const content = await file.text()
			if (!content.trim()) throw new Error('文件是空的')
			const title = extractTitleFromNmn(content, file.name.replace(/\.(spnmn|txt)$/i, ''))
			const scoreId = await insertScore(title, content)
			props.onOpenScore(scoreId)
		} catch (err: any) {
			void appAlert({title: '导入失败', message: String(err?.message ?? err), confirmText: '知道了'})
		} finally {
			setImporting(false)
			if (spnmnInputRef.current) spnmnInputRef.current.value = ''
		}
	}

	// ---------- 图片识别入库 ----------
	const imageInputRef = useRef<HTMLInputElement>(null)
	const [recognizing, setRecognizing] = useState(false)

	async function handleImageRecognize(evt: React.ChangeEvent<HTMLInputElement>) {
		const file = evt.target.files?.[0]
		if (!file) return
		setRecognizing(true)
		try {
			// VLM 按月计次，先查后做
			const quota = await checkQuota('vlm_recognize')
			if (!quota.allowed) {
				void appAlert({title: '配额不足', message: quotaMessage('vlm_recognize', quota), confirmText: '知道了'})
				return
			}
			const content = await recognizeScoreImage(file)
			const title = extractTitleFromNmn(content, file.name.replace(/\.[^.]+$/, '') + '（识别）')
			// 识别已经花钱跑完了，存不进去也不能把结果丢掉 —— 先落成本地文件兜底，
			// 否则用户白等几十秒还要重新识别一次
			let scoreId: string
			try {
				scoreId = await insertScore(title, content)
			} catch (err: any) {
				downloadSpnmn(title, content)
				throw new Error((err?.message ?? err)
					+ '。识别结果已存成下载文件，可用编曲练习库顶部的「导入本地谱」把它收进来。')
			}
			await recordUsage('vlm_recognize')
			// 列表是缓存的（只在挂载/改排序时重取），不刷一下的话从编辑器退回编曲练习库
			// 会看不到刚存进去这首
			await load()
			props.onOpenScore(scoreId)
		} catch (err: any) {
			void appAlert({title: '识别失败', message: String(err?.message ?? err), confirmText: '知道了'})
		} finally {
			setRecognizing(false)
			if (imageInputRef.current) imageInputRef.current.value = ''
		}
	}

	/** 识别结果兜底下载：入库失败时至少别让用户白识别一次 */
	function downloadSpnmn(title: string, content: string) {
		const blob = new Blob([content], {type: 'text/plain;charset=utf-8'})
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = (title || '识别结果').replace(/[\\/:*?"<>|]/g, '_') + '.spnmn'
		a.click()
		URL.revokeObjectURL(url)
	}

	// ---------- 导入 MusicXML（所有登录用户）----------
	// 原先挂在编辑器顶栏的「文件▾」里、所有登录用户都能点；2026-10-29 按要求搬到本页，
	// 中途一度收紧成管理员专属，同日放开给所有人 —— 会员对比表里写着这条权益，
	// 只管理员能用等于卖了个空。
	// 导入 = 建一份**新谱**再打开（本页没有编辑器缓冲区），所以不会出现编辑器里
	// 「覆盖到上一份曲谱」那类问题，也就不需要清云端身份那一套。
	// 配额照走 insertScore → checkQuota('create_score')：免费 20 首 / Plus 200 首，
	// 满了给的是升级提示，不是静默失败。
	const musicXmlInputRef = useRef<HTMLInputElement>(null)
	const [importingXml, setImportingXml] = useState(false)

	async function handleMusicXmlImport(evt: React.ChangeEvent<HTMLInputElement>) {
		const file = evt.target.files?.[0]
		if (!file) return
		setImportingXml(true)
		try {
			const xmlText = await file.text()
			const res = musicXmlToNmn(xmlText)
			const title = res.title || extractTitleFromNmn(res.content, file.name.replace(/\.(musicxml|xml)$/i, ''))
			const scoreId = await insertScore(title, res.content)
			// 列表是缓存的（只在挂载/改排序时重取），不刷一下从新谱退回编曲练习库会看不到它
			await load()
			// 时值提示不写进曲谱源码（`T:` 与音乐片段互斥，塞进去就是语法错误），
			// 改为在这里列出来 —— 用户得知道哪些地方被近似了，否则只会觉得
			// 「导入出来的谱子怪怪的」却找不到原因。
			if (res.warnings.length > 0) {
				void appAlert({
					title: 'MusicXML 已导入，但有时值提示',
					message: res.warnings.slice(0, 8).join('\n')
						+ (res.warnings.length > 8 ? `\n……另有 ${res.warnings.length - 8} 条` : ''),
					confirmText: '知道了',
				})
			}
			props.onOpenScore(scoreId)
		} catch (err: any) {
			void appAlert({title: 'MusicXML 导入失败', message: String(err?.message ?? err), confirmText: '知道了'})
		} finally {
			setImportingXml(false)
			if (musicXmlInputRef.current) musicXmlInputRef.current.value = ''
		}
	}

	/** 入库共用：记录 create_score 用量并返回新曲谱 id */
	async function insertScore(title: string, content: string): Promise<string> {
		const quota = await checkQuota('create_score')
		if (!quota.allowed) throw new Error(quotaMessage('create_score', quota))
		// 记账由后端在 saveNewScore 内部完成（见 views_scores.py 的 score_create），
		// 前端再记一次会重复扣配额。
		return cloud.saveNewScore(title, content)
	}

	// 前端过滤 + 排序（排序走数据库，搜索走前端）
	const filtered = (scores ?? []).filter(s =>
		(!search || s.title.toLowerCase().includes(search.toLowerCase()))
		&& (catTab == 'all' || (catTab == 'course' ? (s.category || 'mine') == 'course' : (s.category || 'mine') == 'mine'))
	)
	// 「跟我练100」分类：课次正序（从小到大）。保存时是逐课按正序写入，但列表
	// 默认按 updated_at 倒序，后存的课次大反而排前面；这里按标题「第 N 课」的
	// 课次号重排，跨阶段再按阶段号（标题里没有阶段号，用课次号 + 更新时间兜底）。
	if (catTab == 'course') {
		filtered.sort((a, b) => {
			const na = courseLessonNo(a.title)
			const nb = courseLessonNo(b.title)
			if (na != nb) return na - nb
			// 同课次（跨阶段）再按创建时间正序，保持保存进来的先后
			return (a.created_at ?? '').localeCompare(b.created_at ?? '')
		})
	}

	// 额度提示：已用 = 库里现有条数（后端 check_quota 也是 `count(*) from scores`，
	// 口径一致）；上限来自 /auth/me 的 plan.max_scores。别在前端写死 20 / 200。
	const used = scores?.length ?? 0
	const scoreLimit = scoreLimitOf(props.user)
	const plus = isPlusUser(props.user)
	const full = used >= scoreLimit

	return <PageHeader
		doc="编曲练习库"
		saveState={scores ? `云上 ${scores.length} 首` : '读取中…'}
		actions={[
			{key: 'new', label: '＋ 新建曲谱', variant: 'primary' as const, mobileRow: true, onClick: createScore},
			// 导入 MusicXML：从编辑器「文件▾」搬过来的入口。2026-10-29 先收紧成管理员专属，
			// 同日晚按产品要求**放开给所有人** —— 它是"把已有的谱搬进来"的正式通道，
			// 也是会员对比表里写着的权益，不能只管理员能用。
			// 放在「新建曲谱」旁边：两者都是"往库里加一份新谱"，别让它掉到末尾去。
			{key: 'importXml', label: importingXml ? '导入中…' : '导入 MusicXML…', variant: 'outline' as const, onClick: () => musicXmlInputRef.current?.click(), disabled: importingXml},
			// 「导入本地谱」「图片识别」仍按产品要求对**所有用户**隐藏（2026-10-29）：
			// 这两条都是"把站外文件弄进库"的通道，运营上不要了。
			// 实现（两个 file input + handleSpnmnImport / handleImageRecognize）原样留着，
			// 要恢复就把下面这两行放回来 —— 别再重写一遍。
			// {key: 'import', label: importing ? '导入中…' : '导入本地谱', variant: 'outline' as const, onClick: () => spnmnInputRef.current?.click(), disabled: importing},
			// {key: 'recognize', label: recognizing ? '识别中…' : '图片识别', variant: 'outline' as const, onClick: () => imageInputRef.current?.click(), disabled: recognizing},
			// 与编辑器顶栏同一动作：新开标签页读教程，不打断本页
			{key: 'tutorial', label: '教程', href: '#tutorial' as const, newTab: true},
			// 管理入口只对管理员出现。放在最后 —— 它是低频动作，
			// 不该跟「新建曲谱」抢顶栏前排的位置
			...(props.isAdmin && props.onAdmin
				? [{key: 'admin', label: '管理', variant: 'outline' as const, onClick: props.onAdmin}]
				: []),
			// 「设置」下拉：把麦克风 / 音色校准 / 个人设置 收进二级菜单，不挤占顶栏。
			// 个人设置放在末尾并用分隔线隔开（它是低频动作，且是跳转到另一页）。
			{key: 'settings', label: '设置', variant: 'outline' as const, menu: [
				{label: '开启麦克风', onClick: () => setMicTestOpen(true)},
				{label: '音色校准', onClick: () => setCalibrating(true)},
				...(props.onProfile
					? [{label: '个人设置', separatorBefore: true, onClick: props.onProfile}]
					: []),
			]},
		]}
		onHome={props.onHome}
		trailing={<span className={classes.userBadge}>{displayName(props.user)}</span>}
		// 退出由顶栏摆在邮箱右侧（原来它是 actions 里的一项，会被排到邮箱左边去）
		onLogout={props.onLogout}>
		{/* 隐藏文件选择器：.spnmn 导入 / 简谱图片识别 —— 两者入口已隐藏，见 actions 注释 */}
		<input ref={spnmnInputRef} type="file" accept=".spnmn,.spnmn.txt,.txt"
			style={{display: 'none'}} onChange={handleSpnmnImport} />
		<input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
			style={{display: 'none'}} onChange={handleImageRecognize} />
		<input ref={musicXmlInputRef} type="file" accept=".xml,.musicxml,text/xml,application/xml"
			style={{display: 'none'}} onChange={handleMusicXmlImport} />
		<div className={classes.lib}>
			{localDraft && <div className={classes.migrate}>
				<span>检测到浏览器里有一份未登录时暂存的曲谱，可导入到你的编曲练习库。</span>
				<button className={tbClass(classes, 'primary')} onClick={migrateLocalDraft} disabled={migrating}>
					{migrating ? '导入中…' : '导入编曲练习库'}
				</button>
			</div>}
			<div className={classes.bar}>
				{/* 第一行：标题 + 额度（左），搜索 + 排序（右）。 */}
				<div className={classes.barTop}>
					<span className={classes.barTitle}>编曲练习库</span>
					<span className={classes.barDiv} aria-hidden="true" />
					{/* 额度 + 会员标识。点开会员弹窗：免费用户看对比与价格，
					    Plus 用户续费（顺延），无期限 Plus 只看到"长期有效"。
					    读列表失败（scores 为 null）时只显示会员名 —— 显示「0/20」
					    会让人以为库被清空了 */}
					<span
						className={`${classes.quota} ${classes.quotaClickable}`}
						role="button"
						tabIndex={0}
						title={plus
							// 有到期时间就写出来：付费用户最常问的一句是"我什么时候到期"
							? (props.user?.plan_expires_at
								? `Plus 会员 · 有效期至 ${fmtExpiry(props.user.plan_expires_at)} · 点击续费`
								: 'Plus 会员 · 长期有效')
							: '查看会员权益'}
						onClick={() => setUpgradeOpen(true)}
						onKeyDown={e => {
							if(e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setUpgradeOpen(true) }
						}}
					>
						<span className={`${classes.planTag} ${plus ? classes.planTagPlus : ''}`}>
							{planLabel(props.user)}
						</span>
						{scores && <span>
							<span className={`${classes.quotaNum} ${full ? classes.quotaNumFull : ''}`}>{used}</span>
							{' / '}{scoreLimit} 首
						</span>}
					</span>
					<label className={classes.search}>
						<svg className={classes.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none"
							stroke="currentColor" strokeWidth="2" aria-hidden="true">
							<circle cx="11" cy="11" r="7" />
							<path d="m20 20-3.5-3.5" />
						</svg>
						<input className={classes.searchInput} type="search" value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder="按曲名搜索" aria-label="按曲名搜索" />
					</label>
					<span className={classes.sortWrap}>
						<select className={`${tbClass(classes, 'outline')} ${classes.sort}`} value={sortBy}
							aria-label="排序方式"
							onChange={e => setSortBy(e.target.value as any)}>
							<option value="updated_at">按修改时间</option>
							<option value="created_at">按创建时间</option>
							<option value="title">按名称</option>
						</select>
						<span className={classes.sortArrow} aria-hidden="true">▼</span>
					</span>
				</div>
				{/* 第二行：分类页签（全部 / 我的曲谱 / 跟我练100）。 */}
				<div className={classes.catTabs} role="tablist" aria-label="曲谱分类">
					<button
						role="tab"
						aria-selected={catTab == 'all'}
						className={classes.catTab + (catTab == 'all' ? ' ' + classes.catTabOn : '')}
						onClick={() => setCatTab('all')}>
						全部
					</button>
					<button
						role="tab"
						aria-selected={catTab == 'mine'}
						className={classes.catTab + (catTab == 'mine' ? ' ' + classes.catTabOn : '')}
						onClick={() => setCatTab('mine')}>
						我的曲谱
					</button>
					<button
						role="tab"
						aria-selected={catTab == 'course'}
						className={classes.catTab + (catTab == 'course' ? ' ' + classes.catTabOn : '')}
						onClick={() => setCatTab('course')}>
						跟我练100
					</button>
				</div>
			</div>
			{scores === null ? (
				<div className={classes.empty}>加载中…</div>
			) : filtered.length === 0 ? (
				<div className={classes.empty}>
					{scores.length === 0
						? '编曲练习库还是空的，点「新建曲谱」开始创作，或在编辑器里保存第一份曲谱。'
						: catTab == 'course'
							? '还没有从「跟我练100」保存进来的课程。去首页「跟我练」页签，点某个阶段右侧的「保存本阶段」即可把整阶段课程收进来。'
							: '没有匹配的曲谱。'}
				</div>
			) : (
				<div className={classes.grid}>
					{filtered.map(score => (
						<div key={score.id} className={classes.card} onClick={() => props.onOpenScore(score.id)}>
							<div className={classes.cardTitle} title={score.title}>{score.title}</div>
							<div className={classes.cardMeta}>
								修改于 {fmtTime(score.updated_at)}
								{(score.category || 'mine') == 'course' && <span className={classes.courseBadge}>跟我练</span>}
								{score.is_public && <span className={classes.pubBadge}>已公开</span>}
							</div>
							<div className={classes.cardActs}>
								<button className={classes.act}
									onClick={e => { e.stopPropagation(); props.onOpenScore(score.id) }}>
									打开
								</button>
								<button className={classes.act}
									onClick={e => { e.stopPropagation(); renameScore(score.id, score.title) }}>
									重命名
								</button>
								<button className={classes.act}
									onClick={e => { e.stopPropagation(); copyScore(score.id, score.title) }}>
									创建副本
								</button>
								<button className={`${classes.act} ${classes.actPublic}`}
									onClick={e => { e.stopPropagation(); requestPublic(score) }}>
									{score.is_public ? '设为私密' : '公开到广场'}
								</button>
								<button className={`${classes.act} ${classes.actDanger}`}
									onClick={e => { e.stopPropagation(); deleteScore(score.id, score.title) }}>
									删除
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			{/* 页脚放在 `lib` **里面**，不是 `PageHeader` 的 children 里：
			    ① `inner` 自己没有横向内边距，直接挂上去页脚会贴到视口左边；
			       `lib` 的 22px 正好让页脚与上面的卡片列左对齐。
			    ② `lib` 是普通块级（没有 `flex:1`），页脚接在内容之后不会挤掉任何东西
			       —— 与编辑器那种「谱面吃满屏」的情况不同，这里不受 `SiteFooter` 顶部
			       那条警告的约束。
			    代价：编曲练习库为空时页脚跟着内容走、不钉在视口底部。因为 `marginTop:'auto'`
			    只在 flex/grid 容器里吸收剩余空间，块级容器里不生效 —— 想要钉底就得
			    把页脚挪成 `children` 的直接子节点并另包一层内边距，两件事要一起做。 */}
			<SiteFooter />
			{/* 站内确认/提示对话框宿主（删除曲谱等）；与编辑器页同一套宣纸/暗墨弹窗 */}
			<AppDialogHost />
			{/* 升级对比：免费会员点额度徽章打开 */}
			<UpgradeDialog
				open={upgradeOpen}
				feature="generic"
				user={props.user}
				onClose={() => setUpgradeOpen(false)}
			/>
			{/* 「设置」菜单里的两个弹窗 */}
			{micTestOpen && <MicTestDialog onClose={() => setMicTestOpen(false)} />}
			{calibrating && <CalibratePanel
				onClose={() => setCalibrating(false)}
				onSave={savePitchCalibration}
			/>}
		</div>
	</PageHeader>
}
